export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const ACCESS_TOKEN_KEY = 'vendiz.accessToken';
const REFRESH_TOKEN_KEY = 'vendiz.refreshToken';

export class ApiError extends Error {
  constructor(public problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }
}

type RequestOptions = RequestInit & { retryAfterRefresh?: boolean };
let refreshRequest: Promise<boolean> | null = null;

function storeSession(payload: { accessToken: string; refreshToken: string }) {
  localStorage.setItem(ACCESS_TOKEN_KEY, payload.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
}

export function clearSession() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

async function refreshSession(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return false;
    const session = await response.json() as { accessToken: string; refreshToken: string };
    storeSession(session);
    return true;
  } catch {
    return false;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { retryAfterRefresh = true, ...init } = options;
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const isFormData = init.body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401 && retryAfterRefresh && path !== '/api/v1/auth/login' && path !== '/api/v1/auth/refresh') {
    refreshRequest ??= refreshSession().finally(() => { refreshRequest = null; });
    if (await refreshRequest) return request<T>(path, { ...init, retryAfterRefresh: false });
    clearSession();
    window.dispatchEvent(new Event('vendiz:session-expired'));
  }

  if (!response.ok) {
    const problem = await response.json().catch(() => ({ title: 'Request failed', status: response.status })) as ProblemDetails;
    throw new ApiError(problem);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function requestBlob(path: string, retryAfterRefresh = true): Promise<Blob> {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const response = await fetch(`${API_BASE_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (response.status === 401 && retryAfterRefresh) {
    refreshRequest ??= refreshSession().finally(() => { refreshRequest = null; });
    if (await refreshRequest) return requestBlob(path, false);
    clearSession();
    window.dispatchEvent(new Event('vendiz:session-expired'));
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => ({ title: 'Download failed', status: response.status })) as ProblemDetails;
    throw new ApiError(problem);
  }
  return response.blob();
}

export function toQuery(params: Record<string, string | number | boolean | undefined | null>) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  });
  const value = query.toString();
  return value ? `?${value}` : '';
}

export const http = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, headers?: HeadersInit) => request<T>(path, { method: 'POST', headers, body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  blob: (path: string) => requestBlob(path),
};
